import { Country, State } from 'country-state-city';

const REGION_SUFFIX_RE = /\s+(Region|District|Province)$/i;
// country-state-city names diverge from Shopify only for these (e.g. NZ
// "Auckland Region" vs Shopify "Auckland"). Extend cautiously — only when
// the divergence is "name + English suffix".
const SUFFIX_STRIP_COUNTRIES = new Set(['NZ']);

/**
 * Strip the " Region"/" District"/" Province" suffix for NZ; passthrough otherwise.
 *
 * @param {string|undefined} name State name as returned by country-state-city
 * @param {string|undefined} countryCode ISO country code (e.g. "NZ", "AU")
 * @returns {string|undefined} Display/save-ready state name
 */
export const formatStateName = (name, countryCode) => {
  if (!name || !countryCode || !SUFFIX_STRIP_COUNTRIES.has(countryCode)) return name;
  return name.replace(REGION_SUFFIX_RE, '').trim();
};

/**
 * Look up a state by code and return its name in Shopify form.
 *
 * @param {string} countryCode ISO country code (e.g. "NZ")
 * @param {string} stateCode   ISO state code (e.g. "AUK")
 * @returns {string|undefined} State name, or undefined if unknown
 */
export const getStateName = (countryCode, stateCode) => {
  if (!countryCode || !stateCode) return undefined;
  return formatStateName(
    State.getStateByCodeAndCountry(stateCode, countryCode)?.name,
    countryCode,
  );
};

/**
 * Resolve a stored province string to its ISO state code. Tries exact match,
 * then for NZ also tries a suffix-stripped match.
 *
 * @param {string|undefined} countryName Country display name (e.g. "New Zealand")
 * @param {string|undefined} province    Stored province string (either form)
 * @returns {string|undefined} ISO state code, or undefined if no match
 */
export const getStateCode = (countryName, province) => {
  if (!countryName || !province) return undefined;
  const countryCode = Country.getAllCountries()
    .find((c) => c.name === countryName)?.isoCode;
  if (!countryCode) return undefined;

  const states = State.getStatesOfCountry(countryCode);
  const exact = states.find((s) => s.name === province)?.isoCode;
  if (exact) return exact;

  if (!SUFFIX_STRIP_COUNTRIES.has(countryCode)) return undefined;

  return states.find(
    (s) => s.name.replace(REGION_SUFFIX_RE, '').trim() === province,
  )?.isoCode;
};
